import { useState } from 'react';
import Seo from '../../components/Seo';
import RelatedTools from '../../components/RelatedTools';

function RandomGenerator() {
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [count, setCount] = useState(1);
  const [allowDuplicates, setAllowDuplicates] = useState(true);
  const [results, setResults] = useState([]);

  const generate = (e) => {
    e.preventDefault();
    const lo = Math.min(Number(min), Number(max));
    const hi = Math.max(Number(min), Number(max));
    const n = Math.max(1, Math.min(Number(count), 1000));

    if (!allowDuplicates && n > (hi - lo + 1)) {
      setResults(['Not enough unique numbers in range']);
      return;
    }

    if (allowDuplicates) {
      const nums = Array.from({ length: n }, () => Math.floor(Math.random() * (hi - lo + 1)) + lo);
      setResults(nums);
    } else {
      const pool = [];
      for (let i = lo; i <= hi; i++) pool.push(i);
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      setResults(pool.slice(0, n));
    }
  };

  const copyResults = () => {
    navigator.clipboard.writeText(results.join(', '));
  };

  return (
    <div>
      <Seo title="Random Number Generator" description="Free random number generator. Generate random numbers in any range with options for quantity and unique numbers." faqs={[{ q: 'Can I generate negative numbers or decimal numbers?', a: 'The generator supports any integer range, including negative numbers. Setting the minimum to -50 and maximum to 50 will produce random integers anywhere in that span, including zero. However, the tool generates whole numbers only -- if you need decimal values, you can generate integers in a larger range and divide by a power of 10 manually. For instance, generating numbers from 1 to 1000 and dividing by 10 gives you values from 0.1 to 100.0.' }, { q: 'How many numbers can I generate at once?', a: 'You can generate up to 1,000 numbers in a single batch. For unique (no-duplicate) results, the maximum is also limited by the size of your range -- you cannot draw 500 unique numbers from a range of only 100. If you need larger datasets, simply run the generator multiple times and combine the results. The "Copy" button makes it easy to paste each batch into a spreadsheet or text file.' }, { q: 'Are the results truly random?', a: 'The numbers are generated using JavaScript\'s Math.random(), which provides pseudorandom values with a uniform distribution. This means every integer in your range has an equal probability of being selected on each draw. While pseudorandom generators are not suitable for cryptographic purposes, they are perfectly reliable for raffles, games, classroom exercises, software testing, and everyday random selection tasks.' }]} />
      <h1>Random Number Generator</h1>
      <p className="subtitle">Generate random numbers within any range.</p>

      <form onSubmit={generate} className="form">
        <div className="input-row">
          <div className="input-group">
            <label>Minimum</label>
            <input type="number" value={min} onChange={(e) => setMin(e.target.value)} required />
          </div>
          <div className="input-group">
            <label>Maximum</label>
            <input type="number" value={max} onChange={(e) => setMax(e.target.value)} required />
          </div>
          <div className="input-group">
            <label>How many?</label>
            <input type="number" min="1" max="1000" value={count} onChange={(e) => setCount(e.target.value)} required />
          </div>
        </div>
        <label className="checkbox-label">
          <input type="checkbox" checked={allowDuplicates} onChange={(e) => setAllowDuplicates(e.target.checked)} />
          Allow duplicates
        </label>
        <button type="submit">Generate</button>
      </form>

      {results.length > 0 && (
        <div className="results">
          <div className="output-header">
            <strong>Results ({results.length})</strong>
            <button onClick={copyResults} className="copy-btn">Copy</button>
          </div>
          <div className="random-results">
            {results.map((num, i) => (
              <span key={i} className="random-num">{num}</span>
            ))}
          </div>
        </div>
      )}

      <section className="info-section">
        <h2>How to Use the Random Number Generator</h2>
        <p>Generating random numbers is quick and flexible. Enter a minimum value and a maximum value to define your range -- for example, 1 and 100 for a classic random pick, or 1000 and 9999 for a four-digit PIN. Set how many numbers you want (up to 1,000 at a time). By default, duplicates are allowed, meaning the same number can appear more than once. If you need every result to be unique -- for instance, when drawing raffle winners -- uncheck the "Allow duplicates" box. Then click "Generate" and your numbers appear instantly. Use the "Copy" button to copy all results to your clipboard, separated by commas, for easy pasting into spreadsheets or messages.</p>

        <h2>How the Generator Works</h2>
        <p>When duplicates are allowed, each number is independently generated by calculating a random integer within your specified range using JavaScript's Math.random() function. When duplicates are disabled, the tool builds an array of every integer in the range, shuffles it using the Fisher-Yates algorithm (a well-known method for producing unbiased permutations), and then takes the first N values from the shuffled array. This guarantees every number in the output is unique. If you request more unique numbers than the range can provide (for example, asking for 20 unique numbers from a range of 1 to 10), the tool alerts you that there are not enough unique numbers available.</p>

        <h3>Can I generate negative numbers or decimal numbers?</h3>
        <p>The generator supports any integer range, including negative numbers. Setting the minimum to -50 and maximum to 50 will produce random integers anywhere in that span, including zero. However, the tool generates whole numbers only -- if you need decimal values, you can generate integers in a larger range and divide by a power of 10 manually. For instance, generating numbers from 1 to 1000 and dividing by 10 gives you values from 0.1 to 100.0.</p>

        <h3>How many numbers can I generate at once?</h3>
        <p>You can generate up to 1,000 numbers in a single batch. For unique (no-duplicate) results, the maximum is also limited by the size of your range -- you cannot draw 500 unique numbers from a range of only 100. If you need larger datasets, simply run the generator multiple times and combine the results. The "Copy" button makes it easy to paste each batch into a spreadsheet or text file.</p>

        <h3>Are the results truly random?</h3>
        <p>The numbers are generated using JavaScript's Math.random(), which provides pseudorandom values with a uniform distribution. This means every integer in your range has an equal probability of being selected on each draw. While pseudorandom generators are not suitable for cryptographic purposes, they are perfectly reliable for raffles, games, classroom exercises, software testing, and everyday random selection tasks.</p>
      </section>
      <RelatedTools current="/random-number-generator" />
    </div>
  );
}

export default RandomGenerator;
